// GET /api/plaid/items -- item-LEVEL connection health, per the M1 execution
// plan's interface table and this plan's §4 rule 2 / §9.6: the shell's
// SyncHealthStrip must read item status here, not filter
// `account.status !== 'ok'` (a value the accounts endpoint can never emit).
//
// WS-0 owns this file's schema and query only (read-only). Mutations
// (`usePlaidLinkToken`, `useExchangePublicToken`, `useItemMutations`,
// `useRemoveItem`) belong to WS-3 in their own file, so no file is shared
// between workstreams (execution plan §8, WS-0 step 10).
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const itemStatusSchema = z.enum([
  'active',
  'needs_login',
  'pending_expiration',
  'revoked',
  'error',
]);

export const providerItemSchema = z.object({
  item_id: z.string(),
  institution_id: z.string().nullable(),
  institution_name: z.string().nullable(),
  status: itemStatusSchema,
  error_code: z.string().nullable(),
  consent_expires_at: z.string().nullable(),
  last_synced_at: z.string().nullable(),
  last_webhook_at: z.string().nullable(),
  account_count: z.number(),
  bootstrap_status: z.enum(['queued', 'running', 'complete', 'failed']),
});

export type ItemStatus = z.infer<typeof itemStatusSchema>;
export type ProviderItem = z.infer<typeof providerItemSchema>;

// `data` IS the array, matching every other collection endpoint.
const itemsResponseSchema = z.array(providerItemSchema);

export function useItems() {
  return useQuery({
    queryKey: ['plaid', 'items'],
    queryFn: async () => {
      const response = await client.get('/api/plaid/items');
      return parseEnvelope(itemsResponseSchema, response.data).data;
    },
  });
}
